import React from 'react';
import {
  Flame, Zap, TrendingUp, Clock, Shield,
  Brain, Target, Hourglass, AlertTriangle, Lock
} from 'lucide-react';

const BADGE_ICONS = {
  Flame, Zap, TrendingUp, Clock, Shield,
  Brain, Target, Hourglass, AlertTriangle,
};

// Positive badges get green accent, negative get red/amber
const BADGE_ACCENT = {
  revenge_trader: 'border-red-500/30 bg-red-500/5',
  fomo_entry: 'border-amber-500/30 bg-amber-500/5',
  overtrader_after_losses: 'border-red-500/30 bg-red-500/5',
  strong_london_session: 'border-green-500/30 bg-green-500/5',
  strong_ny_session: 'border-green-500/30 bg-green-500/5',
  discipline_streak: 'border-green-500/30 bg-green-500/5',
  emotional_awareness: 'border-blue-500/30 bg-blue-500/5',
  stop_loss_mover: 'border-red-500/30 bg-red-500/5',
  patience_master: 'border-green-500/30 bg-green-500/5',
  tilt_prone: 'border-amber-500/30 bg-amber-500/5',
};

const BADGE_ICON_COLOR = {
  revenge_trader: 'text-red-400',
  fomo_entry: 'text-amber-400',
  overtrader_after_losses: 'text-red-400',
  strong_london_session: 'text-green-400',
  strong_ny_session: 'text-green-400',
  discipline_streak: 'text-green-400',
  emotional_awareness: 'text-blue-400',
  stop_loss_mover: 'text-red-400',
  patience_master: 'text-green-400',
  tilt_prone: 'text-amber-400',
};

const PatternBadges = ({ data = [] }) => {
  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <h3 className="text-sm font-semibold text-foreground mb-4">Psychological Pattern Badges</h3>

      {data.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
          Badges will appear as you journal more trades.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.map((badge) => {
            const IconComponent = BADGE_ICONS[badge.icon] || Shield;
            const accentClass = badge.earned
              ? (BADGE_ACCENT[badge.badge_type] || 'border-border bg-muted/30')
              : 'border-border bg-muted/10 opacity-50';
            const iconColor = badge.earned
              ? (BADGE_ICON_COLOR[badge.badge_type] || 'text-muted-foreground')
              : 'text-muted-foreground/50';

            return (
              <div
                key={badge.badge_type}
                className={`relative border rounded-lg p-3 transition-all ${accentClass} ${
                  badge.earned ? 'hover:scale-[1.02] cursor-pointer' : ''
                }`}
              >
                {/* Lock overlay for unearned */}
                {!badge.earned && (
                  <div className="absolute top-2 right-2">
                    <Lock className="w-3 h-3 text-muted-foreground/40" />
                  </div>
                )}

                {/* Icon */}
                <div className="mb-2">
                  <IconComponent className={`w-5 h-5 ${iconColor}`} />
                </div>

                {/* Label */}
                <h4 className={`text-xs font-semibold mb-1 ${badge.earned ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {badge.badge_label}
                </h4>

                {/* Description */}
                <p className="text-[10px] text-muted-foreground leading-tight">
                  {badge.earned ? badge.description : badge.locked_description}
                </p>

                {/* Earned date & evidence */}
                {badge.earned && badge.detected_at && (
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">
                      Earned {new Date(badge.detected_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    {badge.evidence_count > 0 && (
                      <span className="text-[10px] text-muted-foreground">
                        {badge.evidence_count} entries
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PatternBadges;
